import React from 'react';
import { Box, TextField } from '@mui/material';
import { Formik, Form, getIn } from 'formik';
import type { FormikProps } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import es from 'date-fns/esm/locale/es';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ReCAPTCHA from 'react-google-recaptcha';
import type { PLRAlertState } from '../../../types/preloan';
import type { FormSchemaV3 } from '../../../types/preloan/v3';
import { setCapsToName } from '../../../utils/helpers';
import analytics from '../../../utils/analytics';
import personalInfoValidationSchema from './validationSchema';
import PlrModal from '../v3-redesign/landing/PlrModal';
import PlrOptions from '../v3-redesign/landing/PlrOptions';
import AgentLink from '../v3-redesign/landing/AgentLink';
import {
  PlrButton, PlrFieldLabel, PlrNotice, plrInputSx,
} from '../v3-redesign/landing/ui';
import { plr } from '../v3-redesign/landing/tokens';

// Avisos del paso RUT (crédito activo, solicitud vigente, RUT sin información, persona
// jurídica) en una hoja del rediseño. Mismos casos, textos, acciones y eventos que
// PLRAlertDialog (flujo anterior), que sigue usándose en el flujo original.

type PersonalInfo = {
  firstName: string,
  lastName: string,
  birthDate: Date | null,
  sex: string,
  invisibleRecaptchaToken: string,
};

type Props = {
  alertState: PLRAlertState,
  email: string,
  formSchema: FormSchemaV3,
  handlePostPersonalInfo: (personalInfo: PersonalInfo) => void,
};

const TITLES: Record<string, string> = {
  userHasActiveLoan: 'Crédito activo encontrado',
  userHasSignedLoanRequest: 'Solicitud de crédito firmada encontrada',
  userHasUnsignedLoanRequest: 'Solicitud de crédito sin firmar encontrada',
  userHasMissingPersonalInfo: '¡Necesitamos tu ayuda!',
  companyOwnerNotAllowed: 'RUT de persona jurídica',
};

function P({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="p"
      sx={{
        m: 0, fontSize: { xs: 15, sm: 16 }, lineHeight: 1.5, color: plr.body,
      }}
    >
      {children}
    </Box>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return <Box component="strong" sx={{ color: plr.ink, fontWeight: 700 }}>{children}</Box>;
}

const goToAccount = () => {
  analytics.track('Click Boton', { initiator: 'usuario', text: 'Ir a mi cuenta' });
  setTimeout(() => { window.location.href = '/account/login'; }, 1000);
};

const createAccount = () => {
  analytics.track('Click Boton', { initiator: 'usuario', text: 'Crear cuenta' });
  setTimeout(() => { window.location.href = '/account/register'; }, 1000);
};

const reloadPage = () => {
  analytics.track('Click Boton', { initiator: 'usuario', text: 'Recargar pagina' });
  window.location.reload();
};

const fieldError = (form: FormikProps<FormSchemaV3>, name: string) => (
  getIn(form.touched, name) ? (getIn(form.errors, name) as string | undefined) : undefined
);

function PersonalInfoForm({ formSchema, onSubmit }: {
  formSchema: FormSchemaV3,
  onSubmit: (personalInfo: PersonalInfo) => void,
}) {
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [recaptchaError, setRecaptchaError] = React.useState(false);

  return (
    <Formik
      initialValues={formSchema}
      validationSchema={personalInfoValidationSchema}
      onSubmit={async (values) => {
        setRecaptchaError(false);
        if (!recaptchaRef.current) return undefined;
        await recaptchaRef.current.executeAsync();
        const token = recaptchaRef.current.getValue();
        if (!token) return setRecaptchaError(true);
        return onSubmit({
          firstName: values.personalInfo?.firstName.toUpperCase().trim().replace(/\s+/g, ' '),
          lastName: values.personalInfo?.lastName.toUpperCase().trim().replace(/\s+/g, ' '),
          birthDate: values.personalInfo?.birthDate,
          sex: values.personalInfo?.sex,
          invisibleRecaptchaToken: token,
        });
      }}
    >
      {(form) => (
        <Form style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <PlrFieldLabel htmlFor="personalInfo.firstName">Nombre(s)</PlrFieldLabel>
            <TextField
              id="personalInfo.firstName"
              name="personalInfo.firstName"
              autoComplete="off"
              placeholder="Nombre(s)"
              value={form.values.personalInfo.firstName}
              onChange={(e) => form.setFieldValue('personalInfo.firstName', setCapsToName(e.target.value))}
              onBlur={form.handleBlur}
              error={Boolean(fieldError(form, 'personalInfo.firstName'))}
              helperText={fieldError(form, 'personalInfo.firstName')}
              InputProps={{ inputProps: { 'data-pw': 'personal-info-first-name' } }}
              sx={plrInputSx}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <PlrFieldLabel htmlFor="personalInfo.lastName">Apellido(s)</PlrFieldLabel>
            <TextField
              id="personalInfo.lastName"
              name="personalInfo.lastName"
              autoComplete="off"
              placeholder="Apellido(s)"
              value={form.values.personalInfo.lastName}
              onChange={(e) => form.setFieldValue('personalInfo.lastName', setCapsToName(e.target.value))}
              onBlur={form.handleBlur}
              error={Boolean(fieldError(form, 'personalInfo.lastName'))}
              helperText={fieldError(form, 'personalInfo.lastName')}
              InputProps={{ inputProps: { 'data-pw': 'personal-info-last-name' } }}
              sx={plrInputSx}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <PlrFieldLabel htmlFor="personalInfo.birthDate">Fecha de nacimiento</PlrFieldLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                disableFuture
                openTo="year"
                views={['year', 'month', 'day']}
                value={form.values.personalInfo.birthDate}
                onChange={(value) => form.setFieldValue('personalInfo.birthDate', value)}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    id="personalInfo.birthDate"
                    inputProps={{ ...params.inputProps, placeholder: 'DD/MM/AAAA' }}
                    error={Boolean(fieldError(form, 'personalInfo.birthDate'))}
                    helperText={fieldError(form, 'personalInfo.birthDate')}
                    onBlur={() => form.setFieldTouched('personalInfo.birthDate', true, true)}
                    sx={plrInputSx}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box id="personal-info-sex-label" sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 700, color: plr.ink }}>
              Género
            </Box>
            <PlrOptions
              layout="pills"
              labelledBy="personal-info-sex-label"
              testIdPrefix="personal-info-sex"
              options={[{ value: 'F', title: 'Femenino' }, { value: 'M', title: 'Masculino' }]}
              value={(form.values.personalInfo.sex || '') as 'F' | 'M' | ''}
              onChange={(value) => form.setFieldValue('personalInfo.sex', value)}
              error={Boolean(fieldError(form, 'personalInfo.sex'))}
            />
            {fieldError(form, 'personalInfo.sex') && (
              <Box sx={{ mx: '4px', fontSize: 12, color: '#C0392B' }}>{fieldError(form, 'personalInfo.sex')}</Box>
            )}
          </Box>
          <ReCAPTCHA
            size="invisible"
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_INVISIBLE_RECAPTCHA_SITE_KEY as string}
          />
          {recaptchaError && (
            <PlrNotice tone="error" testId="personal-info-captcha-error">
              Ha ocurrido un error al verificar el captcha. Por favor, recarga la página.
            </PlrNotice>
          )}
          <PlrButton
            type="submit"
            testId="personal-info-submit"
            loading={form.isSubmitting}
            disabled={form.isSubmitting}
          >
            Enviar
          </PlrButton>
        </Form>
      )}
    </Formik>
  );
}

function content(props: Props) {
  const {
    alertState, email, formSchema, handlePostPersonalInfo,
  } = props;
  switch (alertState) {
    case 'userHasActiveLoan':
      return (
        <>
          <P>
            {'Según nuestros registros '}
            <Strong>posees un crédito activo con nosotros,</Strong>
            {' por lo que no puedes solicitar otro hasta que termines de pagar el actual. Si necesitas más dinero puedes '}
            <Strong>solicitar un refinanciamiento con aumento del monto del crédito</Strong>
            {' desde tu cuenta banca.me.'}
          </P>
          <PlrButton testId="plr-alert-account" onClick={goToAccount}>Ir a mi cuenta</PlrButton>
          <PlrButton testId="plr-alert-register" variant="outlined" onClick={createAccount}>Crear cuenta</PlrButton>
          <AgentLink />
        </>
      );
    case 'userHasSignedLoanRequest':
      return (
        <>
          <P>
            {'Según nuestros registros '}
            <Strong>posees una solicitud de crédito ya firmada y vigente,</Strong>
            {' por lo que debes esperar '}
            <Strong>a que se acepte en nuestro sistema.</Strong>
            {' Si tienes alguna inquietud, ponte en contacto con nosotros.'}
          </P>
          <AgentLink />
        </>
      );
    case 'userHasUnsignedLoanRequest':
      return (
        <>
          <P>
            {'Según nuestros registros '}
            <Strong>posees una solicitud de crédito sin firmar y vigente,</Strong>
            {' por lo que debes continuar '}
            <Strong>{`desde el enlace que se envió a tu e-mail (${email}).`}</Strong>
            {' Si tienes alguna duda, estaremos felices de ayudarte si nos escribes.'}
          </P>
          <AgentLink />
        </>
      );
    case 'userHasMissingPersonalInfo':
      return (
        <>
          <P>
            <Strong>El RUT ingresado no tiene información personal asociada,</Strong>
            {' por lo que te pedimos llenar los siguientes datos:'}
          </P>
          <PersonalInfoForm formSchema={formSchema} onSubmit={handlePostPersonalInfo} />
          <P>Si tienes alguna duda, estaremos felices de ayudarte si nos escribes.</P>
          <AgentLink />
        </>
      );
    case 'companyOwnerNotAllowed':
      return (
        <>
          <P>
            {'Pareces haber ingresado un RUT de persona jurídica. '}
            <Strong>banca.me solo otorga créditos a personas naturales.</Strong>
            {' Por favor recarga la página y vuelve a intentarlo.'}
          </P>
          <PlrButton testId="plr-alert-reload" onClick={reloadPage}>Recargar página</PlrButton>
        </>
      );
    default:
      return null;
  }
}

export default function PLRAlertSheet(props: Props) {
  const {
    alertState, email, formSchema, handlePostPersonalInfo,
  } = props;
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (alertState) setOpen(true);
  }, [alertState]);

  if (!alertState) return null;

  const close = () => {
    setOpen(false);
    analytics.track('Cerrar Dialog', { initiator: 'usuario' });
  };

  return (
    <>
      <PlrButton
        testId="plr-alert-open"
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ mt: { xs: 'auto', sm: '8px' } }}
      >
        Ver detalle
      </PlrButton>
      <PlrModal
        open={open}
        onClose={close}
        title={TITLES[String(alertState)] || ''}
        titleId="plr-alert-title"
        testId="plr-alert-dialog"
        width={480}
        disableEnforceFocus={alertState === 'userHasMissingPersonalInfo'}
      >
        {content({
          alertState, email, formSchema, handlePostPersonalInfo,
        })}
      </PlrModal>
    </>
  );
}
